import Euler, { EulerViewProps } from '@euler/components/layout';
import { isNotNull } from '@euler/utils';
import {
  Children,
  cloneElement,
  FC,
  isValidElement,
  ReactElement,
  ReactNode,
} from 'react';

export type ReactChildNode = Exclude<ReactNode, boolean | null | undefined>;

export const StackLayout: FC<
  Omit<EulerViewProps, 'flexDirection'> & {
    gap?: number;
    spacing?: number;
    direction?: 'row' | 'row-reverse' | 'column' | 'column-reverse';
    divider?: ReactElement;
  }
> = ({
  gap,
  spacing,
  direction,
  divider,
  justifyContent,
  alignItems,
  children,
  style,
  ...props
}) => {
  direction ??= 'column';

  spacing = spacing ?? gap;

  let elements = Children.toArray(children)
    .filter(isNotNull)
    .filter((el): el is ReactElement => {
      if (isValidElement(el)) return true;
      throw new Error('StackLayout only accepts valid element as children');
    });

  if (divider) {
    elements = arrayJoin(
      ({ index }) =>
        cloneElement(divider as any, { key: `separator-${index}` }),
      elements,
    );
  }

  if (spacing) {
    elements = elements.map((child, idx) => {
      const { key } = child as any;
      if (idx === 0) return child;
      if (!('props' in child)) return child;
      return cloneElement(child, {
        key: key,
        style: [
          child.props.style,
          direction === 'row' && { marginLeft: spacing },
          direction === 'row-reverse' && { marginRight: spacing },
          direction === 'column' && { marginTop: spacing },
          direction === 'column-reverse' && { marginBottom: spacing },
        ],
      });
    });
  }

  return (
    <Euler.View
      style={[
        { justifyContent, alignItems },
        style,
        { flexDirection: direction },
      ]}
      {...props}
    >
      {elements}
    </Euler.View>
  );
};

function arrayJoin<T>(
  separator: (info: { index: number }) => T,
  arr: T[],
): T[] {
  return arr.reduce((res: T[], child, index) => {
    res.push(child);

    if (index < arr.length - 1) {
      res.push(separator({ index }));
    }

    return res;
  }, []);
}
